
import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer
} from 'recharts';
import {
  Heart, CheckCircle2, Globe, Zap, Compass, Wind, Sparkles, UserCheck, SlidersHorizontal, RefreshCw, MessageCircle, LogOut, Info, Target, Trash2, Brain, Users, Tag, TrendingUp, TrendingDown, Minus, Calendar, Check, AlertCircle, Plus, X, Edit3, Bookmark, Activity
} from 'lucide-react';
import { JournalEntry, LifeContext, Language, AIPersonality, Pattern, Decision, Screen, AlignmentFilter, UserProgress, AIPersonalityKey } from '../types';
import { UI_STRINGS } from '../constants/translations';
import { calculateContextQuality } from '../services/openaiService';
import LogoutModal from '../components/LogoutModal';
import { trackPageView, trackLogout, trackInsightViewed } from '../src/lib/analytics';
import AnimatedNumber from '../components/AnimatedNumber';
import DimensionPulseCard from '../components/DimensionPulseCard';
import EmotionFlowChart from '../components/EmotionFlowChart';
import WeeklyRhythmChart from '../components/WeeklyRhythmChart';
import GoalProgressRing from '../components/GoalProgressRing';
import GoalImpactTimeline from '../components/GoalImpactTimeline';
import InsightCard from '../components/InsightCard';
import PatternClusterCard from '../components/PatternClusterCard';
import ProgressDashboard from '../components/ProgressDashboard';
import {
  getDimensionTrends,
  getEmotionFlowData,
  getWeeklyRhythmData,
  generateNarrativeInsights,
  getGoalProgress
} from '../services/insightsGenerator';
import { useFeatureDiscovery } from '../contexts/FeatureDiscoveryContext';
import FeatureDiscovery from '../components/FeatureDiscovery';
import SwipeableFeedbackCard from '../components/SwipeableFeedbackCard';
import EnhancedBlockerCard from '../components/EnhancedBlockerCard';
import SwipeablePatternCard from '../components/SwipeablePatternCard';
import { PersonalityHeaderButton, PersonalitySelectorModal } from '../components/PersonalitySelector';
import { DEFAULT_PERSONALITY_KEY } from '../shared/aiPersonalities';

interface InsightsViewProps {
  context: LifeContext;
  entries: JournalEntry[];
  patterns?: Pattern[];
  decisions?: Decision[];
  userProgress?: UserProgress | null;
  onUpdateContext: (ctx: LifeContext) => void;
  onRefreshPatterns?: () => void;
  onNavigate?: (screen: 'consult' | 'pulse' | 'archive' | 'decisions' | 'insights' | 'profile' | 'evolution') => void;
  onNavigateWithFilter?: (screen: Screen, filter?: AlignmentFilter) => void;
  onPersonalityChange?: (key: AIPersonalityKey) => Promise<void>;
}

const LANGUAGES: {id: Language, label: string, flag: string}[] = [
  { id: 'Italian', label: 'Italiano', flag: '🇮🇹' },
  { id: 'English', label: 'English', flag: '🇺🇸' },
  { id: 'Spanish', label: 'Español', flag: '🇪🇸' },
  { id: 'German', label: 'Deutsch', flag: '🇩🇪' },
  { id: 'French', label: 'Français', flag: '🇫🇷' },
];

// Remove diacritics from string (pure function for use in initialization)
const removeDiacriticsUtil = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

// Normalize name for consistent key matching (pure function for use in initialization)
const normalizeNameUtil = (name: string): string => {
  let n = name.toLowerCase().trim();
  n = removeDiacriticsUtil(n);
  // Remove articles in multiple languages
  n = n.replace(/^(il |lo |la |l'|l'|i |gli |le |un |uno |una |un')/, '');
  n = n.replace(/^(el |la |los |las |un |una |unos |unas )/, '');
  n = n.replace(/^(le |la |l'|l'|les |un |une |des )/, '');
  n = n.replace(/^(der |die |das |den |dem |des |ein |eine |einer |eines )/, '');
  n = n.replace(/^(the |a |an )/, '');
  n = n.replace(/'s$/, '');
  n = n.replace(/s'$/, '');
  n = n.replace(/([a-z]{3,})i$/, '$1o');
  n = n.replace(/([a-z]{3,})e$/, '$1a');
  return n.trim();
};

// Migrate people data from old format to new normalized keys
const migratePeopleData = () => {
  const migrationKey = 'people_data_migrated_v2';
  if (localStorage.getItem(migrationKey)) return; // Already migrated
  
  try {
    // Migrate aliases
    const aliasesRaw = localStorage.getItem('people_aliases');
    if (aliasesRaw) {
      const oldAliases = JSON.parse(aliasesRaw) as Record<string, string>;
      const newAliases: Record<string, string> = {};
      Object.entries(oldAliases).forEach(([key, value]) => {
        const newKey = normalizeNameUtil(key);
        newAliases[newKey] = value;
        // Keep old key too for backward compat
        if (key !== newKey) newAliases[key] = value;
      });
      localStorage.setItem('people_aliases', JSON.stringify(newAliases));
    }
    
    // Migrate relations
    const relationsRaw = localStorage.getItem('people_relations');
    if (relationsRaw) {
      const oldRelations = JSON.parse(relationsRaw) as Record<string, string>;
      const newRelations: Record<string, string> = {};
      Object.entries(oldRelations).forEach(([key, value]) => {
        const newKey = normalizeNameUtil(key);
        newRelations[newKey] = value;
        // Keep old key too for backward compat
        if (key !== newKey) newRelations[key] = value;
      });
      localStorage.setItem('people_relations', JSON.stringify(newRelations));
    }
    
    // Migrate hidden people
    const hiddenRaw = localStorage.getItem('hidden_people');
    if (hiddenRaw) {
      const oldHidden = JSON.parse(hiddenRaw) as string[];
      const newHidden = new Set<string>();
      oldHidden.forEach(key => {
        newHidden.add(normalizeNameUtil(key));
        newHidden.add(key); // Keep old key too
      });
      localStorage.setItem('hidden_people', JSON.stringify(Array.from(newHidden)));
    }
    
    localStorage.setItem(migrationKey, 'true');
  } catch (e) {
    console.error('Failed to migrate people data:', e);
  }
};

// Run migration on module load
migratePeopleData();

// Delete modal translations
const DELETE_MODAL_TRANSLATIONS = {
  Italian: {
    title: 'Elimina Tutti i Dati',
    subtitle: 'Questa azione è irreversibile',
    description: 'Tutti i tuoi momenti, pattern, decisioni e progressi verranno eliminati permanentemente. Il tuo account rimarrà attivo ma dovrai ricominciare da zero.',
    confirm: 'Elimina Tutto',
    cancel: 'Annulla',
    deleting: 'Eliminazione in corso...',
  },
  English: {
    title: 'Delete All Data',
    subtitle: 'This action is irreversible',
    description: 'All your moments, patterns, decisions, and progress will be permanently deleted. Your account will remain active but you will have to start from scratch.',
    confirm: 'Delete Everything',
    cancel: 'Cancel',
    deleting: 'Deleting...',
  },
  Spanish: {
    title: 'Eliminar Todos los Datos',
    subtitle: 'Esta acción es irreversible',
    description: 'Todos tus momentos, patrones, decisiones y progreso serán eliminados permanentemente. Tu cuenta permanecerá activa pero tendrás que empezar desde cero.',
    confirm: 'Eliminar Todo',
    cancel: 'Cancelar',
    deleting: 'Eliminando...',
  },
  German: {
    title: 'Alle Daten Löschen',
    subtitle: 'Diese Aktion ist unwiderruflich',
    description: 'Alle deine Momente, Muster, Entscheidungen und Fortschritte werden dauerhaft gelöscht. Dein Konto bleibt aktiv, aber du musst von vorne beginnen.',
    confirm: 'Alles Löschen',
    cancel: 'Abbrechen',
    deleting: 'Wird gelöscht...',
  },
  French: {
    title: 'Supprimer Toutes les Données',
    subtitle: 'Cette action est irréversible',
    description: 'Tous tes moments, schémas, décisions et progrès seront supprimés définitivement. Ton compte restera actif mais tu devras tout recommencer.',
    confirm: 'Tout Supprimer',
    cancel: 'Annuler',
    deleting: 'Suppression en cours...',
  },
};

const InsightsView: React.FC<InsightsViewProps> = ({ context, entries, patterns = [], decisions = [], userProgress, onUpdateContext, onRefreshPatterns, onNavigate, onNavigateWithFilter, onPersonalityChange }) => {
  const t = useMemo(() => UI_STRINGS[context.language] || UI_STRINGS.English, [context.language]);
  const deleteT = DELETE_MODAL_TRANSLATIONS[context.language] || DELETE_MODAL_TRANSLATIONS.English;
  const [activeTab, setActiveTab] = useState<'profile' | 'flow' | 'personality' | 'settings'>('profile');
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const discoveryFallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { state: discoveryState, getNextStepForView, completeStep } = useFeatureDiscovery();
  const discoveryStateRef = useRef(discoveryState);
  const completeStepRef = useRef(completeStep);
  const currentDiscoveryStep = useMemo(() => getNextStepForView('insights'), [getNextStepForView]);

  // AI Personality state
  const [isPersonalityModalOpen, setIsPersonalityModalOpen] = useState(false);
  const currentPersonalityKey = context.selectedPersonalityKey || DEFAULT_PERSONALITY_KEY;

  const handleDiscoveryActivate = useCallback((step: keyof typeof discoveryState) => {
    if (discoveryFallbackTimerRef.current) {
      clearTimeout(discoveryFallbackTimerRef.current);
    }
    
    if (step === 'insightsDiscovered') {
      completeStep(step);
    } else if (step === 'valuesDiscovered') {
      setActiveTab('profile');
      setEditingValues(true);
      setEditingGoals(false);
      setTimeout(() => {
        const input = document.getElementById('value-input') as HTMLInputElement;
        if (input) input.focus();
      }, 300);
      discoveryFallbackTimerRef.current = setTimeout(() => {
        if (!discoveryStateRef.current.valuesDiscovered) {
          completeStepRef.current('valuesDiscovered');
        }
      }, 20000);
    } else if (step === 'goalsDiscovered') {
      setActiveTab('profile');
      setEditingGoals(true);
      setEditingValues(false);
      setTimeout(() => {
        const input = document.getElementById('goal-input') as HTMLInputElement;
        if (input) input.focus();
      }, 300);
      discoveryFallbackTimerRef.current = setTimeout(() => {
        if (!discoveryStateRef.current.goalsDiscovered) {
          completeStepRef.current('goalsDiscovered');
        }
      }, 20000);
    }
  }, [completeStep]);

  // Keep refs updated for use in timeouts
  useEffect(() => {
    discoveryStateRef.current = discoveryState;
    completeStepRef.current = completeStep;
  }, [discoveryState, completeStep]);

  useEffect(() => {
    trackPageView('/insights', 'Insights', true);
    return () => {
      if (discoveryFallbackTimerRef.current) {
        clearTimeout(discoveryFallbackTimerRef.current);
      }
    };
  }, []);

  // Handle scroll to section from navigation (e.g., from HomeView pattern click)
  useEffect(() => {
    const scrollTarget = localStorage.getItem('insights_scroll_target');
    if (scrollTarget) {
      localStorage.removeItem('insights_scroll_target');
      // Small delay to ensure the view is rendered
      setTimeout(() => {
        const element = document.getElementById(scrollTarget);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  // Handle tab change with scroll to top (desktop optimization)
  const handleTabChange = useCallback((tab: 'profile' | 'flow' | 'personality' | 'settings') => {
    setActiveTab(tab);
    trackInsightViewed(tab);
    // Scroll content area to top when changing tabs
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);
  
  const handleLogout = () => {
    trackLogout();
    localStorage.removeItem('life_context_v3');
    localStorage.removeItem('context_entries_v5');
    localStorage.removeItem('context_decisions_v3');
    localStorage.removeItem('context_patterns_v1');
    window.location.href = '/api/logout';
  };

  const handleDeleteAllData = async () => {
    try {
      setIsDeleting(true);
      const response = await fetch('/api/user/data', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        // Clear local storage
        localStorage.clear();
        // Reload to show onboarding
        window.location.reload();
      } else {
        console.error('Failed to delete data');
        setIsDeleting(false);
        setIsDeleteOpen(false);
      }
    } catch (error) {
      console.error('Error deleting data:', error);
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  const [editingValues, setEditingValues] = useState(false);
  const [editingGoals, setEditingGoals] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [newGoal, setNewGoal] = useState('');
  const [editingPeople, setEditingPeople] = useState(false);
  const [peopleAliases, setPeopleAliases] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem('people_aliases');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [peopleRelations, setPeopleRelations] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem('people_relations');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [hiddenPeople, setHiddenPeople] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('hidden_people');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [manualPeople, setManualPeople] = useState<Array<{name: string; relationship?: string}>>(() => {
    try {
      const saved = localStorage.getItem('manual_people');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [newPersonName, setNewPersonName] = useState('');
  const [newPersonRelation, setNewPersonRelation] = useState('');

  // State for rejected blockers feedback
  const [rejectedBlockers, setRejectedBlockers] = useState<Set<string>>(new Set());

  // Load rejected blockers on mount
  useEffect(() => {
    const loadRejected = async () => {
      try {
        const res = await fetch('/api/insight-feedback/rejected');
        if (res.ok) {
          const data = await res.json();
          setRejectedBlockers(new Set(data.rejectedBlockers || []));
        }
      } catch (e) {
        console.error('Failed to load rejected blockers:', e);
      }
    };
    loadRejected();
  }, []);

  // Handle blocker feedback
  const handleBlockerFeedback = useCallback(async (
    id: string,
    content: string,
    feedbackType: 'helpful' | 'not_helpful'
  ) => {
    try {
      await fetch('/api/insight-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insightType: 'blocker',
          insightContent: content,
          feedbackType,
          relatedEntityId: id,
          relatedEntityType: 'blocker',
          contextSnapshot: {
            profileVersion: context.aiProfile?.version,
            timestamp: Date.now()
          }
        })
      });

      // If rejected, add to local state immediately
      if (feedbackType === 'not_helpful') {
        setRejectedBlockers(prev => new Set([...prev, content]));
      }
    } catch (e) {
      console.error('Failed to submit blocker feedback:', e);
    }
  }, [context.aiProfile?.version]);

  // Filter visible blockers (exclude rejected ones)
  const visibleBlockers = useMemo(() => {
    if (!context.aiProfile?.blockers) return [];
    return context.aiProfile.blockers
      .filter(b => b.status === 'active')
      .filter(b => !rejectedBlockers.has(b.blocker));
  }, [context.aiProfile?.blockers, rejectedBlockers]);

  // State for rejected patterns feedback
  const [rejectedPatterns, setRejectedPatterns] = useState<Set<string>>(new Set());

  // Load rejected patterns on mount
  useEffect(() => {
    const loadRejectedPatterns = async () => {
      try {
        const res = await fetch('/api/insight-feedback/rejected');
        if (res.ok) {
          const data = await res.json();
          setRejectedPatterns(new Set(data.rejectedPatterns || []));
        }
      } catch (e) {
        console.error('Failed to load rejected patterns:', e);
      }
    };
    loadRejectedPatterns();
  }, []);

  // Handle pattern feedback
  const handlePatternFeedback = useCallback(async (
    patternId: string,
    patternTitle: string,
    feedbackType: 'helpful' | 'not_helpful'
  ) => {
    try {
      await fetch('/api/insight-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insightType: 'pattern',
          insightContent: patternTitle,
          feedbackType,
          relatedEntityId: patternId,
          relatedEntityType: 'pattern',
          contextSnapshot: {
            profileVersion: context.aiProfile?.version,
            timestamp: Date.now()
          }
        })
      });

      // If rejected, add to local state immediately
      if (feedbackType === 'not_helpful') {
        setRejectedPatterns(prev => new Set([...prev, patternId]));
      }
    } catch (e) {
      console.error('Failed to submit pattern feedback:', e);
    }
  }, [context.aiProfile?.version]);

  // Filter visible patterns (exclude rejected ones)
  const visiblePatterns = useMemo(() => {
    return patterns.filter(p => !rejectedPatterns.has(p.id));
  }, [patterns, rejectedPatterns]);

  const defaultPersonality: AIPersonality = { archetypeId: 'companion', warmth: 80, directness: 50, depth: 50 };
  const currentPersonality = context.aiPersonality || defaultPersonality;

  // Calculate context quality
  const contextQuality = useMemo(() =>
    calculateContextQuality(context, entries, patterns, decisions),
    [context, entries.length, patterns.length, decisions.length]
  );

  // Use the pure utility function for diacritics removal
  const removeDiacritics = removeDiacriticsUtil;

  // Multilingual stoplist: pronouns, self-references, and relationship terms to exclude
  // All entries are normalized (lowercase, no diacritics) for consistent matching
  const PEOPLE_STOPLIST = useMemo(() => {
    const rawList = [
      // Italian pronouns/self-references
      'io', 'me', 'mi', 'noi', 'ci', 'se', 'sé', 'stesso', 'stessa',
      // English pronouns/self-references  
      'i', 'my', 'myself', 'we', 'us', 'our', 'ourselves', 'you', 'yourself',
      // Spanish pronouns
      'yo', 'mí', 'nosotros', 'nosotras', 'usted', 'ustedes',
      // German pronouns
      'ich', 'mich', 'mir', 'wir', 'uns', 'selbst', 'du', 'dich', 'dir',
      // French pronouns
      'je', 'moi', 'nous', 'tu', 'toi', 'vous', 'soi',
      // Italian relationship terms (used as references, not names)
      'moglie', 'marito', 'mamma', 'papà', 'papa', 'madre', 'padre', 'figlio', 'figlia',
      'fratello', 'sorella', 'nonno', 'nonna', 'zio', 'zia', 'cugino', 'cugina',
      'suocero', 'suocera', 'genero', 'nuora', 'cognato', 'cognata',
      // English relationship terms
      'wife', 'husband', 'mom', 'mum', 'mother', 'dad', 'father', 'son', 'daughter',
      'brother', 'sister', 'grandpa', 'grandma', 'grandfather', 'grandmother',
      'uncle', 'aunt', 'cousin', 'nephew', 'niece',
      // Spanish relationship terms
      'esposa', 'esposo', 'mamá', 'madre', 'papá', 'padre', 'hijo', 'hija',
      'hermano', 'hermana', 'abuelo', 'abuela', 'tío', 'tía', 'primo', 'prima',
      // German relationship terms
      'frau', 'mann', 'mutter', 'mutti', 'vater', 'vati', 'sohn', 'tochter',
      'bruder', 'schwester', 'opa', 'oma', 'onkel', 'tante',
      // French relationship terms
      'femme', 'mari', 'mère', 'maman', 'père', 'papa', 'fils', 'fille',
      'frère', 'sœur', 'soeur', 'grand-père', 'grand-mère', 'oncle', 'tante',
      // Generic terms
      'friend', 'amico', 'amica', 'amici', 'amiche', 'ami', 'amie', 'freund', 'freundin',
      'colleague', 'collega', 'colleghi', 'colleghe', 'kollege', 'kollegin',
      'boss', 'capo', 'chef', 'jefe', 'jefa',
      'doctor', 'dottore', 'dottoressa', 'médecin', 'arzt', 'ärztin', 'médico',
    ];
    // Normalize all entries to match against normalized names
    return new Set(rawList.map(w => removeDiacritics(w.toLowerCase().trim())));
  }, []);

  // Enhanced normalize name: remove articles, diacritics, normalize plural/singular
  const normalizeName = (name: string): string => {
    let n = name.toLowerCase().trim();
    // Remove diacritics for consistent matching
    n = removeDiacritics(n);
    // Remove articles in multiple languages
    // Italian
    n = n.replace(/^(il |lo |la |l'|l'|i |gli |le |un |uno |una |un')/, '');
    // Spanish
    n = n.replace(/^(el |la |los |las |un |una |unos |unas )/, '');
    // French
    n = n.replace(/^(le |la |l'|l'|les |un |une |des )/, '');
    // German
    n = n.replace(/^(der |die |das |den |dem |des |ein |eine |einer |eines )/, '');
    // English
    n = n.replace(/^(the |a |an )/, '');
    // Remove possessives
    n = n.replace(/'s$/, '');
    n = n.replace(/s'$/, '');
    // Normalize Italian plural/singular
    n = n.replace(/([a-z]{3,})i$/, '$1o'); // medici -> medico
    n = n.replace(/([a-z]{3,})e$/, '$1a'); // colleghe -> collega
    return n.trim();
  };

  // Check if a name should be excluded (pronoun, self-reference, or generic term)
  const shouldExcludeName = (name: string): boolean => {
    const normalized = normalizeName(name);
    return PEOPLE_STOPLIST.has(normalized) || normalized.length < 2;
  };

  // Enhanced similarity check with fuzzy matching
  const areSimilar = (a: string, b: string): boolean => {
    if (a === b) return true;
    // Remove diacritics for comparison
    const aNorm = removeDiacritics(a);
    const bNorm = removeDiacritics(b);
    if (aNorm === bNorm) return true;
    // Check if one is contained in the other (for nicknames)
    if (aNorm.includes(bNorm) || bNorm.includes(aNorm)) return true;
    // Check if they share the same root (first 4+ chars)
    if (aNorm.length >= 4 && bNorm.length >= 4 && aNorm.substring(0, 4) === bNorm.substring(0, 4)) return true;
    // Levenshtein-like simple check: if only 1-2 chars different and same length
    if (Math.abs(aNorm.length - bNorm.length) <= 1 && aNorm.length >= 4) {
      let diffs = 0;
      const maxLen = Math.max(aNorm.length, bNorm.length);
      for (let i = 0; i < maxLen; i++) {
        if (aNorm[i] !== bNorm[i]) diffs++;
        if (diffs > 2) return false;
      }
      return diffs <= 1;
    }
    return false;
  };

  // Get relation for a person, checking all aliases (with backward compatibility)
  const getPersonRelation = (person: { aliases?: string[] }): string => {
    if (!person.aliases) return '';
    for (const alias of person.aliases) {
      // Check normalized key first
      if (peopleRelations[alias]) return peopleRelations[alias];
      // Backward compatibility: check without diacritics removal (old format)
      const oldStyleKey = alias.toLowerCase().trim();
      if (peopleRelations[oldStyleKey]) return peopleRelations[oldStyleKey];
    }
    return '';
  };

  // Check if a person is hidden (with backward compatibility for old keys)
  const isPersonHidden = (key: string): boolean => {
    if (hiddenPeople.includes(key)) return true;
    // Backward compatibility: check old-style key
    const oldStyleKey = key.toLowerCase().trim();
    return hiddenPeople.includes(oldStyleKey);
  };

  // Extract unique people with smart matching and aliases
  const peopleMentioned = useMemo(() => {
    const peopleMap = new Map<string, { name: string; count: number; relationships: Set<string>; originalNames: Set<string> }>();
    
    entries.forEach(e => {
      if (e.people) {
        e.people.forEach(p => {
          // Skip pronouns, self-references, and generic relationship terms
          if (shouldExcludeName(p.name)) return;
          
          const normalizedName = normalizeName(p.name);
          const displayName = peopleAliases[normalizedName] || p.name;
          const existing = peopleMap.get(normalizedName);
          if (existing) {
            existing.count++;
            if (p.relationship) existing.relationships.add(p.relationship);
            existing.originalNames.add(p.name);
          } else {
            peopleMap.set(normalizedName, {
              name: displayName,
              count: 1,
              relationships: new Set(p.relationship ? [p.relationship] : []),
              originalNames: new Set([p.name])
            });
          }
        });
      }
    });
    
    // Smart matching: merge similar normalized names
    const merged = new Map<string, { name: string; count: number; relationships: Set<string>; aliases: string[] }>();
    peopleMap.forEach((data, key) => {
      if (isPersonHidden(key)) return;
      
      // Check if this name is similar to any existing merged name
      let matchedKey: string | null = null;
      merged.forEach((existingData, existingKey) => {
        if (areSimilar(key, existingKey)) {
          matchedKey = existingKey;
        }
      });
      
      if (matchedKey && merged.has(matchedKey)) {
        const existing = merged.get(matchedKey)!;
        existing.count += data.count;
        data.relationships.forEach(r => existing.relationships.add(r));
        existing.aliases.push(key);
      } else {
        // Use the most common original name as display
        const bestName = peopleAliases[key] || Array.from(data.originalNames)[0];
        merged.set(key, { name: bestName, count: data.count, relationships: data.relationships, aliases: [key] });
      }
    });
    
    // Add manual people to the list (manual people bypass the stoplist)
    manualPeople.forEach(mp => {
      const normalizedName = normalizeName(mp.name);
      if (isPersonHidden(normalizedName)) return;
      
      if (!merged.has(normalizedName)) {
        merged.set(normalizedName, {
          name: mp.name,
          count: 0,
          relationships: new Set(mp.relationship ? [mp.relationship] : []),
          aliases: [normalizedName]
        });
      }
    });
    
    return Array.from(merged.values()).sort((a, b) => b.count - a.count);
  }, [entries, peopleAliases, hiddenPeople, manualPeople, shouldExcludeName, normalizeName, areSimilar, isPersonHidden]);

  const savePeoplePrefs = (aliases: Record<string, string>, hidden: string[], relations?: Record<string, string>) => {
    setPeopleAliases(aliases);
    setHiddenPeople(hidden);
    localStorage.setItem('people_aliases', JSON.stringify(aliases));
    localStorage.setItem('hidden_people', JSON.stringify(hidden));
    if (relations !== undefined) {
      setPeopleRelations(relations);
      localStorage.setItem('people_relations', JSON.stringify(relations));
    }
  };

  const updatePersonRelation = (personKey: string, relation: string) => {
    const newRelations = { ...peopleRelations, [personKey]: relation };
    setPeopleRelations(newRelations);
    localStorage.setItem('people_relations', JSON.stringify(newRelations));
  };

  const addManualPerson = () => {
    if (!newPersonName.trim()) return;
    const newPerson = { name: newPersonName.trim(), relationship: newPersonRelation.trim() || undefined };
    const updated = [...manualPeople, newPerson];
    setManualPeople(updated);
    localStorage.setItem('manual_people', JSON.stringify(updated));
    setNewPersonName('');
    setNewPersonRelation('');
  };

  const removeManualPerson = (name: string) => {
    const updated = manualPeople.filter(p => p.name !== name);
    setManualPeople(updated);
    localStorage.setItem('manual_people', JSON.stringify(updated));
  };

  // Calculate category stats
  const categoryStats = useMemo(() => {
    const stats: Record<string, number> = {};
    entries.forEach(e => {
      stats[e.category] = (stats[e.category] || 0) + 1;
    });
    return Object.entries(stats)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
  }, [entries]);

  // Calculate emotional patterns
  const emotionStats = useMemo(() => {
    const stats: Record<string, number> = {};
    entries.forEach(e => {
      stats[e.emotion] = (stats[e.emotion] || 0) + 1;
    });
    return Object.entries(stats)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
  }, [entries]);

  const updatePersonality = (key: keyof AIPersonality, val: any) => {
    onUpdateContext({
      ...context,
      aiPersonality: { ...currentPersonality, [key]: val }
    });
  };

  const radarData = useMemo(() => {
    const categoryMappings = [
      { keys: ['Salute', 'Health', 'Santé', 'Gesundheit', 'Salud'], display: t.categories.Health },
      { keys: ['Focus', 'Concentration', 'Konzentration', 'Enfoque'], display: t.categories.Focus },
      { keys: ['Connessione', 'Connection', 'Connexion', 'Verbindung', 'Conexión'], display: t.categories.Connection },
      { keys: ['Spirito', 'Spirit', 'Esprit', 'Geist', 'Espíritu'], display: t.categories.Spirit },
      { keys: ['Finanza', 'Finance', 'Finances', 'Finanzen', 'Finanzas'], display: t.categories.Finance }
    ];
    
    return categoryMappings.map(({ keys, display }) => {
      const catEntries = entries.filter(e => keys.some(k => k.toLowerCase() === (e.category || '').toLowerCase()));
      const avg = catEntries.length > 0 
        ? Math.round(catEntries.reduce((acc, e) => acc + (e.goalAlignment || 50), 0) / catEntries.length)
        : 30;
      return { subject: display, A: avg };
    });
  }, [entries, t]);

  const averageAlignment = useMemo(() => {
    if (entries.length === 0) return 0;
    return Math.round(entries.reduce((acc, e) => acc + (e.goalAlignment || 50), 0) / entries.length);
  }, [entries]);

  const dimensionTrends = useMemo(() => getDimensionTrends(entries, 14), [entries]);
  
  const emotionFlowData = useMemo(() => getEmotionFlowData(entries, 14, context.language), [entries, context.language]);
  
  const weeklyRhythmData = useMemo(() => getWeeklyRhythmData(entries), [entries]);
  
  const narrativeInsights = useMemo(() => 
    generateNarrativeInsights(entries, patterns, decisions, context, context.language), 
    [entries, patterns, decisions, context]
  );
  
  const goalProgress = useMemo(() => 
    getGoalProgress(entries, context.goals || []), 
    [entries, context.goals]
  );

  const dimensionLabels: Record<string, string> = useMemo(() => ({
    salute: t.categories?.Health || 'Health',
    focus: t.categories?.Focus || 'Focus',
    connessione: t.categories?.Connection || 'Connection',
    spirito: t.categories?.Spirit || 'Spirit',
    finanza: t.categories?.Finance || 'Finance'
  }), [t]);

  return (
    <div className="h-full flex flex-col bg-[#F5F5F7] view-enter overflow-hidden">
      <header className="px-6 lg:px-12 xl:px-16 2xl:px-20 pt-6 lg:pt-6 pb-3 lg:pb-4 shrink-0 bg-white/95 backdrop-blur-xl border-b border-black/[0.03]">
        <div className="max-w-6xl 2xl:max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4 lg:mb-5">
            <h1 className="text-[30px] lg:text-4xl xl:text-[42px] font-black tracking-tighter leading-none">{t.navAnalysis}</h1>
            {/* AI Personality Button */}
            <PersonalityHeaderButton
              personalityKey={currentPersonalityKey}
              language={context.language}
              onClick={() => setIsPersonalityModalOpen(true)}
            />
          </div>
          <div className="flex p-1 lg:p-1.5 bg-black/5 rounded-[18px] lg:rounded-[20px] w-full">
            {['profile', 'flow', 'personality', 'settings'].map((tab) => (
              <button key={tab} onClick={() => handleTabChange(tab as any)} className={`flex-1 px-3 lg:px-6 py-2 lg:py-2.5 rounded-[14px] lg:rounded-[16px] text-[9px] lg:text-xs font-black uppercase tracking-wider transition-all duration-300 ${activeTab === tab ? 'bg-white shadow-lg text-black' : 'text-black/30 hover:text-black/50 hover:bg-white/50'}`}>
                {tab === 'profile' ? t.navProfile : tab === 'flow' ? t.trajectory : tab === 'personality' ? t.insights.tabTone : t.insights.tabConfig}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-6 lg:px-12 xl:px-20 2xl:px-32 py-6 lg:py-12 no-scrollbar pb-32 lg:pb-44">
        <div className="max-w-6xl 2xl:max-w-7xl mx-auto">
        {/* Profile Tab - Your evolving identity */}
        {activeTab === 'profile' && (
          <div className="space-y-6 lg:space-y-8 animate-in fade-in duration-500">
            {/* Progress Dashboard */}
            {userProgress && (
              <ProgressDashboard progress={userProgress} language={context.language} compact={false} />
            )}


            {/* Context Quality Score - Desktop: side by side with stats */}
            <div className="">
              {/* Knowledge Percentage Card - TEMPORARILY DISABLED
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-[32px] lg:rounded-[40px] xl:rounded-[48px] p-6 lg:p-8 xl:p-10 text-white shadow-xl">
                <div className="flex items-center gap-4 mb-5 lg:mb-6">
                  <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-2xl lg:rounded-3xl bg-white/20 flex items-center justify-center">
                    <Brain size={28} className="text-white lg:w-8 lg:h-8" />
                  </div>
                  <div>
                    <p className="text-[11px] lg:text-xs font-bold opacity-70 uppercase tracking-wider">{t.insightsPage.knowYouAt}</p>
                    <p className="text-[36px] lg:text-5xl xl:text-6xl font-black leading-none">{contextQuality.score}%</p>
                  </div>
                </div>
                <div className="h-2.5 lg:h-3 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-1000"
                    style={{ width: `${contextQuality.score}%` }}
                  />
                </div>
                <p className="text-[13px] lg:text-sm font-medium opacity-70 mt-4">
                  {contextQuality.level === 'excellent' ? t.insightsPage.knowDeep :
                   contextQuality.level === 'deep' ? t.insightsPage.niceConnection :
                   contextQuality.level === 'good' ? t.insightsPage.learningAboutYou :
                   contextQuality.level === 'growing' ? t.insightsPage.continueLearning :
                   t.insightsPage.beginningJourney}
                </p>
              </div>
              */}

              {/* Stats Grid - Full width on all screens, 4 columns on desktop */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 xl:gap-5">
                <div className="bg-white rounded-2xl lg:rounded-3xl p-4 lg:p-6 xl:p-8 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                  <div className="flex items-center gap-2 mb-2 lg:mb-4">
                    <Calendar size={16} className="text-indigo-500 lg:w-5 lg:h-5" />
                    <span className="text-[10px] lg:text-xs font-bold text-black/40 uppercase">{t.insightsPage.moments}</span>
                  </div>
                  <p className="text-[28px] lg:text-4xl xl:text-5xl font-black text-black">{entries.length}</p>
                </div>
                <div className="bg-white rounded-2xl lg:rounded-3xl p-4 lg:p-6 xl:p-8 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                  <div className="flex items-center gap-2 mb-2 lg:mb-4">
                    <TrendingUp size={16} className="text-amber-500 lg:w-5 lg:h-5" />
                    <span className="text-[10px] lg:text-xs font-bold text-black/40 uppercase">{t.insightsPage.patterns}</span>
                  </div>
                  <p className="text-[28px] lg:text-4xl xl:text-5xl font-black text-black">{patterns.length}</p>
                </div>
                <div className="bg-white rounded-2xl lg:rounded-3xl p-4 lg:p-6 xl:p-8 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                  <div className="flex items-center gap-2 mb-2 lg:mb-4">
                    <Compass size={16} className="text-emerald-500 lg:w-5 lg:h-5" />
                    <span className="text-[10px] lg:text-xs font-bold text-black/40 uppercase">{t.insightsPage.decisions}</span>
                  </div>
                  <p className="text-[28px] lg:text-4xl xl:text-5xl font-black text-black">{decisions.length}</p>
                </div>
                <div className="bg-white rounded-2xl lg:rounded-3xl p-4 lg:p-6 xl:p-8 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                  <div className="flex items-center gap-2 mb-2 lg:mb-4">
                    <Users size={16} className="text-pink-500 lg:w-5 lg:h-5" />
                    <span className="text-[10px] lg:text-xs font-bold text-black/40 uppercase">{t.insightsPage.people}</span>
                  </div>
                  <p className="text-[28px] lg:text-4xl xl:text-5xl font-black text-black">{peopleMentioned.length}</p>
                </div>
              </div>
            </div>

            {/* AI Profile Summary - Chi Sei - HERO SECTION */}
            {context.aiProfile?.summary && (
              <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 rounded-[28px] lg:rounded-[36px] p-6 lg:p-8 xl:p-10 shadow-2xl text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.08) 0%, transparent 50%)'}}></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4 lg:mb-5">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Sparkles size={22} className="text-white lg:w-6 lg:h-6" />
                    </div>
                    <div>
                      <span className="text-[11px] lg:text-xs font-bold text-white/60 uppercase tracking-wider">
                        {context.language === 'Italian' ? 'Il Tuo Specchio' : 
                         context.language === 'Spanish' ? 'Tu Espejo' :
                         context.language === 'German' ? 'Dein Spiegel' :
                         context.language === 'French' ? 'Ton Miroir' : 'Your Mirror'}
                      </span>
                      <h3 className="text-xl lg:text-2xl font-black leading-tight">
                        {context.language === 'Italian' ? 'Chi Sei' : 
                         context.language === 'Spanish' ? 'Quién Eres' :
                         context.language === 'German' ? 'Wer Du Bist' :
                         context.language === 'French' ? 'Qui Tu Es' : 'Who You Are'}
                      </h3>
                    </div>
                  </div>
                  <p className="text-[16px] lg:text-lg xl:text-xl text-white/95 leading-relaxed font-medium mb-5">
                    {context.aiProfile.summary}
                  </p>
                  {context.aiProfile.personalityTraits && context.aiProfile.personalityTraits.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-white/20">
                      {context.aiProfile.personalityTraits.slice(0, 6).map((trait, i) => (
                        <span key={i} className="px-3 py-1.5 bg-white/15 backdrop-blur-sm text-white text-[11px] lg:text-xs font-bold rounded-full border border-white/20">
                          {trait.trait}
                        </span>
                      ))}
                    </div>
                  )}
                  {context.aiProfile.version && (
                    <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-[10px] lg:text-xs text-white/50">
                      <span>
                        {context.language === 'Italian' ? `Versione ${context.aiProfile.version}` : 
                         context.language === 'Spanish' ? `Versión ${context.aiProfile.version}` :
                         `Version ${context.aiProfile.version}`}
                      </span>
                      <span>
                        {context.language === 'Italian' ? 'Evolve con ogni momento' : 
                         context.language === 'Spanish' ? 'Evoluciona con cada momento' :
                         context.language === 'German' ? 'Entwickelt sich mit jedem Moment' :
                         context.language === 'French' ? 'Évolue avec chaque moment' : 'Evolves with every moment'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Blockers/Challenges Section - Enhanced with severity visualization */}
            {visibleBlockers.length > 0 && (
              <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={16} className="text-amber-600" />
                    <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">
                      {context.language === 'Italian' ? 'Sfide da Affrontare' :
                       context.language === 'Spanish' ? 'Desafíos a Superar' :
                       context.language === 'German' ? 'Herausforderungen' :
                       context.language === 'French' ? 'Défis à Relever' : 'Challenges to Overcome'}
                    </span>
                    <span className="text-[9px] font-bold text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">
                      {visibleBlockers.length}
                    </span>
                  </div>
                  <span className="text-[10px] text-black/30 font-medium lg:hidden">
                    {context.language === 'Italian' ? '← Scorri' :
                     context.language === 'Spanish' ? '← Desliza' :
                     context.language === 'German' ? '← Wischen' :
                     context.language === 'French' ? '← Glissez' :
                     '← Swipe'}
                  </span>
                </div>
                <div className="space-y-3">
                  {visibleBlockers.map((blocker, i) => (
                    <EnhancedBlockerCard
                      key={`blocker-${i}-${blocker.blocker}`}
                      id={`blocker-${i}`}
                      blocker={blocker}
                      onFeedback={handleBlockerFeedback}
                      language={context.language}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Profile Evolution Timeline */}
            {context.aiProfile?.profileHistory && context.aiProfile.profileHistory.length > 1 && (
              <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Activity size={16} className="text-indigo-500" />
                    <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">
                      {context.language === 'Italian' ? 'La Tua Evoluzione' : 
                       context.language === 'Spanish' ? 'Tu Evolución' :
                       context.language === 'German' ? 'Deine Entwicklung' :
                       context.language === 'French' ? 'Ton Évolution' : 'Your Evolution'}
                    </span>
                  </div>
                  {context.aiProfile.profileHistory.length > 1 && (
                    <button 
                      onClick={() => onNavigate?.('evolution')}
                      className="text-[11px] font-bold text-indigo-600 hover:text-indigo-700 transition-colors px-2 py-1 rounded-lg active:bg-indigo-50"
                    >
                      {context.language === 'Italian' ? 'Vedi tutto' : 
                       context.language === 'Spanish' ? 'Ver todo' :
                       context.language === 'German' ? 'Alles anzeigen' :
                       context.language === 'French' ? 'Voir tout' : 'See all'}
                    </button>
                  )}
                </div>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-indigo-500 via-purple-500 to-pink-500 rounded-full" />
                  <div className="space-y-4 pl-10">
                    {context.aiProfile.profileHistory.slice(-3).reverse().map((snapshot, i) => (
                      <div key={i} className={`relative ${i === 0 ? 'opacity-100' : 'opacity-80'}`}>
                        <div className={`absolute -left-[26px] w-3 h-3 rounded-full border-2 border-white shadow ${
                          i === 0 ? 'bg-indigo-500' : 'bg-purple-400'
                        }`} />
                        <div className="bg-gray-50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-bold text-indigo-600 uppercase">
                              v{snapshot.version}
                            </span>
                            <span className="text-[10px] text-black/40">
                              {new Date(snapshot.timestamp).toLocaleDateString(
                                context.language === 'Italian' ? 'it-IT' : 
                                context.language === 'Spanish' ? 'es-ES' :
                                context.language === 'German' ? 'de-DE' :
                                context.language === 'French' ? 'fr-FR' : 'en-US',
                                { day: 'numeric', month: 'short' }
                              )}
                            </span>
                          </div>
                          <p className="text-[13px] text-black/70 leading-relaxed">{snapshot.summary}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Strengths */}
            {contextQuality.strengths.length > 0 && (
              <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <Check size={14} className="text-emerald-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.insightsPage.whatIKnow}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {contextQuality.strengths.map((s, i) => (
                    <span key={i} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-[12px] font-bold rounded-full">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Values - Editable */}
            <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Heart size={14} className="text-emerald-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.insightsPage.yourValues}</span>
                </div>
                <button onClick={() => setEditingValues(!editingValues)} className="p-1.5 hover:bg-gray-50 rounded-lg">
                  <Edit3 size={14} className={editingValues ? "text-indigo-500" : "text-black/30"} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {context.values?.map((v, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full">
                    <span className="text-[12px] font-bold text-emerald-700">{v}</span>
                    {editingValues && (
                      <button onClick={() => onUpdateContext({...context, values: context.values.filter((_, idx) => idx !== i)})} className="text-emerald-400 hover:text-red-500">
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
                {editingValues && (
                  <div className="flex items-center gap-1">
                    <input
                      id="value-input"
                      type="text"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      placeholder={t.insightsPage.newValue}
                      className="px-3 py-1.5 bg-gray-50 rounded-full text-[12px] font-medium outline-none w-32 border border-gray-200 focus:border-indigo-300"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newValue.trim()) {
                          onUpdateContext({...context, values: [...(context.values || []), newValue.trim()]});
                          setNewValue('');
                          if (!discoveryState.valuesDiscovered) {
                            completeStep('valuesDiscovered');
                          }
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        if (newValue.trim()) {
                          onUpdateContext({...context, values: [...(context.values || []), newValue.trim()]});
                          setNewValue('');
                          if (!discoveryState.valuesDiscovered) {
                            completeStep('valuesDiscovered');
                          }
                        }
                      }}
                      className="w-7 h-7 bg-emerald-500 text-white rounded-full flex items-center justify-center"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>
              {(!context.values || context.values.length === 0) && !editingValues && (
                <button onClick={() => setEditingValues(true)} className="text-[12px] text-indigo-500 font-medium">
                  {t.insightsPage.addYourValues}
                </button>
              )}
            </div>

            {/* Goals - Editable */}
            <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Bookmark size={14} className="text-blue-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.insightsPage.yourGoals}</span>
                </div>
                <button onClick={() => setEditingGoals(!editingGoals)} className="p-1.5 hover:bg-gray-50 rounded-lg">
                  <Edit3 size={14} className={editingGoals ? "text-indigo-500" : "text-black/30"} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {context.goals?.map((g, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-full">
                    <span className="text-[12px] font-bold text-blue-700">{g}</span>
                    {editingGoals && (
                      <button onClick={() => onUpdateContext({...context, goals: context.goals.filter((_, idx) => idx !== i)})} className="text-blue-400 hover:text-red-500">
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
                {editingGoals && (
                  <div className="flex items-center gap-1">
                    <input
                      id="goal-input"
                      type="text"
                      value={newGoal}
                      onChange={(e) => setNewGoal(e.target.value)}
                      placeholder={t.insightsPage.newGoal}
                      className="px-3 py-1.5 bg-gray-50 rounded-full text-[12px] font-medium outline-none w-36 border border-gray-200 focus:border-indigo-300"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newGoal.trim()) {
                          onUpdateContext({...context, goals: [...(context.goals || []), newGoal.trim()]});
                          setNewGoal('');
                          if (!discoveryState.goalsDiscovered) {
                            completeStep('goalsDiscovered');
                          }
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        if (newGoal.trim()) {
                          onUpdateContext({...context, goals: [...(context.goals || []), newGoal.trim()]});
                          setNewGoal('');
                          if (!discoveryState.goalsDiscovered) {
                            completeStep('goalsDiscovered');
                          }
                        }
                      }}
                      className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>
              {(!context.goals || context.goals.length === 0) && !editingGoals && (
                <button onClick={() => setEditingGoals(true)} className="text-[12px] text-indigo-500 font-medium">
                  {t.insightsPage.addYourGoals}
                </button>
              )}
            </div>

            {/* Gaps - Improved CTAs */}
            {contextQuality.gaps.length > 0 && (
              <div className="bg-white rounded-[24px] p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle size={14} className="text-amber-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.insightsPage.toKnowYouBetter}</span>
                </div>
                <div className="space-y-2">
                  {contextQuality.gaps.map((g, i) => {
                    const isValuesGap = g.toLowerCase().includes('valor');
                    const isGoalsGap = g.toLowerCase().includes('obiettiv');
                    const isChallengeGap = g.toLowerCase().includes('sfid') || g.toLowerCase().includes('blocc') || g.toLowerCase().includes('difficolt') || g.toLowerCase().includes('problem');
                    return (
                      <button
                        key={i}
                        onClick={() => {
                          if (isValuesGap) {
                            setEditingValues(true);
                          } else if (isGoalsGap) {
                            setEditingGoals(true);
                          } else if (isChallengeGap) {
                            sessionStorage.setItem('newDecisionContext', JSON.stringify({
                              prefill: g,
                              source: 'insights_gap'
                            }));
                            onNavigate?.('decisions');
                          } else {
                            sessionStorage.setItem('chatContext', JSON.stringify({
                              type: 'knowledge_gaps',
                              data: { gapArea: g }
                            }));
                            onNavigate?.('consult');
                          }
                        }}
                        className="w-full flex items-center gap-2 p-3 bg-amber-50/50 rounded-xl active:scale-[0.98] transition-transform text-left"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        <span className="text-[13px] font-medium text-amber-800 flex-1">{g}</span>
                        {isValuesGap || isGoalsGap ? (
                          <Edit3 size={14} className="text-amber-400" />
                        ) : isChallengeGap ? (
                          <Compass size={14} className="text-amber-400" />
                        ) : (
                          <MessageCircle size={14} className="text-amber-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Categories */}
            {categoryStats.length > 0 && (
              <div className="bg-white rounded-[24px] p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Tag size={14} className="text-indigo-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.insights.lifeAreas}</span>
                </div>
                <div className="space-y-2">
                  {categoryStats.map(([cat, count], i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-[13px] font-medium text-black/70">
                        {(t.categories as Record<string, string>)?.[cat] || cat}
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: `${(count / entries.length) * 100}%` }}
                          />
                        </div>
                        <span className="text-[11px] font-bold text-black/30 w-8">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Emotions */}
            {emotionStats.length > 0 && (
              <div className="bg-white rounded-[24px] p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Heart size={14} className="text-pink-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.insights.yourEmotions}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {emotionStats.map(([emoji, count], i) => (
                    <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-full">
                      <span className="text-lg">{emoji}</span>
                      <span className="text-[11px] font-bold text-black/50">{count}×</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Patterns - Premium Visual Grid with Swipeable Feedback */}
            <div id="patterns-section" className="space-y-4 scroll-mt-24">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-amber-500" />
                  <span className="text-[10px] md:text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.insights.patternsIdentified}</span>
                  {visiblePatterns.length > 0 && (
                    <span className="text-[9px] md:text-[10px] font-bold text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">{visiblePatterns.length}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-black/30 font-medium lg:hidden">
                    {context.language === 'Italian' ? '← Scorri' :
                     context.language === 'Spanish' ? '← Desliza' :
                     context.language === 'German' ? '← Wischen' :
                     context.language === 'French' ? '← Glissez' :
                     '← Swipe'}
                  </span>
                  {onRefreshPatterns && visiblePatterns.length > 0 && (
                    <button
                      onClick={onRefreshPatterns}
                      className="p-2 hover:bg-gray-50 rounded-xl transition-all duration-200 active:scale-95"
                    >
                      <RefreshCw size={14} className="text-black/30" />
                    </button>
                  )}
                </div>
              </div>

              {visiblePatterns.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {[...visiblePatterns]
                    .sort((a, b) => b.strength - a.strength)
                    .slice(0, 9)
                    .map((p, i) => {
                      const patternAny = p as any;
                      return (
                        <SwipeablePatternCard
                          key={p.id || i}
                          pattern={{
                            ...p,
                            coreConnection: patternAny.coreConnection
                          }}
                          translations={{
                            timesObserved: t.insights.timesObserved,
                            connectedTo: t.insights.connectedTo,
                            insight: t.insights.patternInsight,
                            typeLabels: {
                              emotional: t.insights.patternTypeEmotional,
                              behavioral: t.insights.patternTypeBehavioral,
                              temporal: t.insights.patternTypeTemporal,
                              relational: t.insights.patternTypeRelational,
                              decision: t.insights.patternTypeDecision
                            }
                          }}
                          onFeedback={handlePatternFeedback}
                          language={context.language}
                        />
                      );
                    })}
                </div>
              ) : (
                <div className="bg-white rounded-[24px] p-8 text-center shadow-sm">
                  <div className="text-4xl mb-3">🔍</div>
                  <p className="text-[13px] text-black/40 font-medium">{t.insights.noPatterns}</p>
                </div>
              )}
            </div>

            {/* People - Simplified Edit Section */}
            {(peopleMentioned.length > 0 || editingPeople) && (
              <div className="bg-white rounded-[24px] p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-pink-500" />
                    <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.insights.importantPeople}</span>
                  </div>
                  <button onClick={() => setEditingPeople(!editingPeople)} className="p-1.5 hover:bg-gray-50 rounded-lg transition-colors">
                    <Edit3 size={14} className={editingPeople ? "text-pink-500" : "text-black/30"} />
                  </button>
                </div>
                <div className="space-y-3">
                  {peopleMentioned.slice(0, editingPeople ? 20 : 10).map((person) => {
                    const personKey = person.aliases?.[0] || person.name.toLowerCase();
                    // Get saved relation checking all aliases for robustness
                    const savedRelation = getPersonRelation(person);
                    const autoRelation = person.relationships.size > 0 ? Array.from(person.relationships)[0] : '';
                    const displayRelation = savedRelation || autoRelation;
                    
                    return (
                      <div key={personKey} className={`rounded-xl p-3 transition-all ${editingPeople ? 'bg-pink-50 border border-pink-100' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center text-pink-600 font-bold text-[13px] shrink-0 mt-0.5">
                            {person.name.charAt(0).toUpperCase()}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            {editingPeople ? (
                              <input
                                key={`name-${personKey}`}
                                type="text"
                                defaultValue={person.name}
                                placeholder={t.insights.namePlaceholder}
                                className="w-full bg-white px-2 py-1 rounded text-[13px] font-bold text-black outline-none border border-pink-200 focus:border-pink-400 mb-2"
                                onBlur={(e) => {
                                  const newName = e.target.value.trim();
                                  if (newName && newName !== person.name) {
                                    const newAliases = { ...peopleAliases };
                                    person.aliases?.forEach(alias => {
                                      newAliases[alias] = newName;
                                    });
                                    savePeoplePrefs(newAliases, hiddenPeople);
                                  }
                                }}
                              />
                            ) : (
                              <div className="text-[13px] font-bold text-black truncate">{person.name}</div>
                            )}
                            
                            {editingPeople ? (
                              <input
                                key={`rel-${personKey}-${savedRelation}`}
                                type="text"
                                defaultValue={savedRelation}
                                placeholder={t.insights.relationshipPlaceholder}
                                className="w-full bg-white px-2 py-1 rounded text-[12px] text-black/70 outline-none border border-pink-100 focus:border-pink-300 mb-2"
                                onBlur={(e) => {
                                  const relation = e.target.value.trim();
                                  if (relation !== savedRelation) {
                                    updatePersonRelation(personKey, relation);
                                  }
                                }}
                              />
                            ) : (
                              <div className="text-[11px] text-pink-500 font-medium min-h-[18px]">
                                {displayRelation || <span className="text-black/30">—</span>}
                              </div>
                            )}
                            
                            <div className="text-[10px] text-black/40">{person.count}× {t.insights.timesMentioned}</div>
                          </div>
                          
                          {editingPeople && (
                            <button
                              onClick={() => {
                                const newHidden = [...hiddenPeople, ...(person.aliases || [])];
                                savePeoplePrefs(peopleAliases, newHidden);
                              }}
                              className="mt-1 p-1.5 text-red-400 hover:bg-red-50 rounded-lg shrink-0 transition-colors"
                            >
                              <X size={16} strokeWidth={2.5} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {editingPeople && (
                  <div className="mt-4 p-3 bg-pink-50 border border-pink-100 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Plus size={14} className="text-pink-500" />
                      <span className="text-[11px] font-bold text-pink-600">{t.insights.addPerson}</span>
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={newPersonName}
                        onChange={(e) => setNewPersonName(e.target.value)}
                        placeholder={t.insights.addPersonPlaceholder}
                        className="w-full bg-white px-3 py-2 rounded-lg text-[13px] text-black outline-none border border-pink-200 focus:border-pink-400"
                      />
                      <input
                        type="text"
                        value={newPersonRelation}
                        onChange={(e) => setNewPersonRelation(e.target.value)}
                        placeholder={t.insights.relationshipPlaceholder}
                        className="w-full bg-white px-3 py-2 rounded-lg text-[12px] text-black/70 outline-none border border-pink-100 focus:border-pink-300"
                      />
                      <button
                        onClick={addManualPerson}
                        disabled={!newPersonName.trim()}
                        className="w-full py-2 bg-pink-500 text-white rounded-lg text-[12px] font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-pink-600 transition-colors"
                      >
                        {t.insights.addPerson}
                      </button>
                    </div>
                  </div>
                )}
                
                {hiddenPeople.length > 0 && editingPeople && (
                  <button
                    onClick={() => savePeoplePrefs(peopleAliases, [])}
                    className="mt-4 text-[11px] text-pink-500 font-medium hover:text-pink-600 transition-colors"
                  >
                    {t.insights.restoreHiddenPeople} ({hiddenPeople.length})
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'flow' && (
          <div className="space-y-6 lg:space-y-8 animate-in fade-in duration-500">

            {/* Narrative Insights Section */}
            {narrativeInsights.length > 0 && (
              <div className="stagger-1">
                {/* Intro Text for Trends */}
                <div className="mb-6 lg:mb-8">
                  <h3 className="text-xl lg:text-2xl xl:text-3xl font-black text-[#1D1D1F] mb-3 lg:mb-4">{t.insights.trendsIntroTitle}</h3>
                  <p className="text-sm lg:text-base text-black/60 leading-relaxed max-w-4xl">{t.insights.trendsIntroDescription}</p>
                </div>

                <div className="flex items-center gap-2 mb-3 md:mb-4">
                  <Activity size={16} className="text-indigo-500 lg:w-5 lg:h-5" />
                  <h3 className="text-[10px] md:text-[11px] lg:text-[12px] font-bold text-black/40 uppercase tracking-wider">{t.insights.yourTrends}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4 lg:gap-5 xl:gap-6">
                  {narrativeInsights.map((insight, i) => (
                    <InsightCard
                      key={i}
                      type={insight.type}
                      title={insight.title}
                      description={insight.description}
                      metric={insight.metric}
                      delay={i * 100}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Alignment Score with Animated Number and Insights */}
            <div className="stagger-2 bg-white rounded-[32px] p-5 lg:p-6 shadow-sm card-glow">
              <div className="flex items-center gap-5">
                <div className="relative w-20 h-20 lg:w-24 lg:h-24 shrink-0">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="50%"
                      cy="50%"
                      r="45%"
                      fill="none"
                      stroke="rgba(99, 102, 241, 0.1)"
                      strokeWidth="6"
                    />
                    <circle
                      cx="50%"
                      cy="50%"
                      r="45%"
                      fill="none"
                      stroke="url(#alignmentGradient)"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray={`${(entries.length > 0 ? averageAlignment : 0) * 2.83} 283`}
                      className="transition-all duration-1000 ease-out"
                    />
                    <defs>
                      <linearGradient id="alignmentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#6366f1" />
                        <stop offset="100%" stopColor="#a855f7" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    {entries.length > 0 ? (
                      <AnimatedNumber 
                        value={averageAlignment} 
                        suffix="%" 
                        className="text-[22px] lg:text-[26px] font-black gradient-text"
                      />
                    ) : (
                      <span className="text-[16px] lg:text-[18px] font-black text-black/20">—</span>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] lg:text-[11px] font-bold uppercase tracking-widest text-black/30">{t.alignmentScore}</span>
                  <p className="text-[15px] lg:text-[17px] font-bold text-black leading-snug mt-0.5">
                    {entries.length === 0 
                      ? (t.insights.noData || 'Inizia a registrare momenti per scoprire il tuo allineamento')
                      : `${t.insights.coreAlignmentMessage} ${averageAlignment > 60 ? t.insights.coreAlignmentExcellent : t.insights.coreAlignmentEvolving}.`
                    }
                  </p>
                </div>
              </div>
              
              {/* Alignment Insights */}
              {entries.length > 0 && (
                <div className="mt-4 pt-4 border-t border-black/5">
                  <div className="grid grid-cols-3 gap-3">
                    <button 
                      onClick={() => onNavigateWithFilter?.('archive', 'aligned')}
                      className="text-center p-2 rounded-xl hover:bg-emerald-50 transition-colors cursor-pointer"
                    >
                      <p className="text-[18px] lg:text-[22px] font-black text-emerald-600">
                        {entries.filter(e => (e.goalAlignment || 50) >= 60).length}
                      </p>
                      <p className="text-[9px] lg:text-[10px] font-bold text-black/40 uppercase">
                        {context.language === 'Italian' ? 'Allineati' : context.language === 'Spanish' ? 'Alineados' : 'Aligned'}
                      </p>
                    </button>
                    <button 
                      onClick={() => onNavigateWithFilter?.('archive', 'neutral')}
                      className="text-center p-2 rounded-xl hover:bg-amber-50 transition-colors cursor-pointer"
                    >
                      <p className="text-[18px] lg:text-[22px] font-black text-amber-500">
                        {entries.filter(e => (e.goalAlignment || 50) > 40 && (e.goalAlignment || 50) < 60).length}
                      </p>
                      <p className="text-[9px] lg:text-[10px] font-bold text-black/40 uppercase">
                        {context.language === 'Italian' ? 'Neutri' : context.language === 'Spanish' ? 'Neutrales' : 'Neutral'}
                      </p>
                    </button>
                    <button 
                      onClick={() => onNavigateWithFilter?.('archive', 'misaligned')}
                      className="text-center p-2 rounded-xl hover:bg-rose-50 transition-colors cursor-pointer"
                    >
                      <p className="text-[18px] lg:text-[22px] font-black text-rose-500">
                        {entries.filter(e => (e.goalAlignment || 50) <= 40).length}
                      </p>
                      <p className="text-[9px] lg:text-[10px] font-bold text-black/40 uppercase">
                        {context.language === 'Italian' ? 'Disallineati' : context.language === 'Spanish' ? 'Desalineados' : 'Misaligned'}
                      </p>
                    </button>
                  </div>
                  
                  {/* Trend indicator - last 7 days vs previous 7 days */}
                  {entries.length >= 3 && (() => {
                    const now = Date.now();
                    const DAY_MS = 1000 * 60 * 60 * 24;
                    const last7Days = entries.filter(e => (now - new Date(e.timestamp).getTime()) < 7 * DAY_MS);
                    const previous7Days = entries.filter(e => {
                      const ts = new Date(e.timestamp).getTime();
                      return (now - ts) >= 7 * DAY_MS && (now - ts) < 14 * DAY_MS;
                    });
                    const recentAvg = last7Days.length > 0 ? last7Days.reduce((acc, e) => acc + (e.goalAlignment || 50), 0) / last7Days.length : 0;
                    const olderAvg = previous7Days.length > 0 ? previous7Days.reduce((acc, e) => acc + (e.goalAlignment || 50), 0) / previous7Days.length : recentAvg;
                    const trend = recentAvg - olderAvg;
                    const trendTexts: Record<string, { up: string; down: string; stable: string; days: string }> = {
                      Italian: { up: 'In crescita', down: 'In calo', stable: 'Stabile', days: '7 giorni' },
                      English: { up: 'Improving', down: 'Declining', stable: 'Stable', days: '7 days' },
                      Spanish: { up: 'En aumento', down: 'Disminuyendo', stable: 'Estable', days: '7 días' },
                      German: { up: 'Steigend', down: 'Sinkend', stable: 'Stabil', days: '7 Tage' },
                      French: { up: 'En hausse', down: 'En baisse', stable: 'Stable', days: '7 jours' }
                    };
                    const langTexts = trendTexts[context.language] || trendTexts.Italian;
                    const trendText = trend > 3 ? langTexts.up : trend < -3 ? langTexts.down : langTexts.stable;
                    return (
                      <div className={`mt-3 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-full text-[10px] font-bold ${
                        trend > 3 ? 'bg-emerald-50 text-emerald-600' :
                        trend < -3 ? 'bg-rose-50 text-rose-500' :
                        'bg-amber-50 text-amber-600'
                      }`}>
                        {trend > 3 ? <TrendingUp size={12} /> : trend < -3 ? <TrendingDown size={12} /> : <Minus size={12} />}
                        <span>{trendText}</span>
                        {Math.abs(trend) > 1 && <span className="opacity-60">({trend > 0 ? '+' : ''}{Math.round(trend)}%)</span>}
                        <span className="text-[8px] opacity-50 ml-1">{langTexts.days}</span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Dimension Pulse Cards */}
            <div className="stagger-3">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <Sparkles size={16} className="text-purple-500" />
                <h3 className="text-[10px] md:text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.insights.fiveDimensions}</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3">
                {(['salute', 'focus', 'connessione', 'spirito', 'finanza'] as const).map((dim, i) => (
                  <DimensionPulseCard
                    key={dim}
                    dimension={dim}
                    data={dimensionTrends[dim] || { current: 0, previous: 0, history: [] }}
                    label={dimensionLabels[dim]}
                    noDataLabel={t.insights.noData}
                    trendLabel={context.language === 'Italian' ? '7 giorni' : context.language === 'Spanish' ? '7 días' : context.language === 'German' ? '7 Tage' : context.language === 'French' ? '7 jours' : '7 days'}
                    delay={i * 80}
                  />
                ))}
              </div>
            </div>

            {/* Emotion Flow Chart */}
            {emotionFlowData.length >= 3 && (
              <div className="stagger-4 bg-white rounded-[24px] md:rounded-[32px] p-4 md:p-6 lg:p-8 shadow-sm card-glow">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 md:mb-6">
                  <div>
                    <h3 className="text-[10px] md:text-[11px] font-bold text-black/40 uppercase tracking-wider mb-1">{t.insights.emotionFlowTitle}</h3>
                    <p className="text-[16px] md:text-[18px] font-black text-black">{t.insights.emotionFlowSubtitle}</p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-[10px] md:text-[11px] text-black/40">{t.insights.lastDays.replace('{n}', '14')}</p>
                    <p className="text-[13px] md:text-[14px] font-bold text-indigo-600">{emotionFlowData.length} {t.insights.emotionFlowDaysTracked}</p>
                  </div>
                </div>
                <EmotionFlowChart data={emotionFlowData} height={180} />
              </div>
            )}

            {/* Goal Progress Rings + Radar Chart Stacked on Desktop */}
            <div className="stagger-5 grid grid-cols-1 gap-4 md:gap-6">
              {/* Goal Progress with Impact Timeline */}
              {(context.goals?.length || 0) > 0 && (
                <div className="bg-white rounded-[24px] md:rounded-[32px] p-4 md:p-6 lg:p-8 shadow-sm card-glow">
                  <div className="mb-4 md:mb-5">
                    <h3 className="text-[10px] md:text-[11px] font-bold text-black/40 uppercase tracking-wider mb-1">{t.insights.goalAlignmentTitle}</h3>
                    <p className="text-[15px] md:text-[17px] font-black text-black">{t.insights.goalAlignmentSubtitle}</p>
                  </div>
                  <GoalProgressRing
                    goals={goalProgress}
                    size={200}
                    translations={{
                      alignment: t.insights.alignment,
                      addGoals: t.insights.addGoals,
                      trending: context.language === 'Spanish' ? 'Tendencia' : context.language === 'English' ? 'Trending' : context.language === 'German' ? 'Trend' : context.language === 'French' ? 'Tendance' : 'Tendenza',
                      showMore: t.insights.showMoreGoals,
                      showLess: t.insights.showLessGoals,
                      selectGoals: t.insights.selectGoals
                    }}
                  />
                  {/* GoalImpactTimeline hidden for now
                  <GoalImpactTimeline
                    entries={entries}
                    goals={context.goals || []}
                    translations={{
                      impactTitle: context.language === 'Spanish' ? 'Momentos clave' : context.language === 'English' ? 'Key moments' : context.language === 'German' ? 'Schlüsselmomente' : context.language === 'French' ? 'Moments clés' : 'Momenti chiave',
                      positiveImpact: context.language === 'Spanish' ? 'Impacto positivo' : context.language === 'English' ? 'Positive impact' : context.language === 'German' ? 'Positive Auswirkung' : context.language === 'French' ? 'Impact positif' : 'Impatto positivo',
                      negativeImpact: context.language === 'Spanish' ? 'Impacto negativo' : context.language === 'English' ? 'Negative impact' : context.language === 'German' ? 'Negative Auswirkung' : context.language === 'French' ? 'Impact négatif' : 'Impatto negativo',
                      noImpacts: context.language === 'Spanish' ? 'Tus momentos aparecerán aquí' : context.language === 'English' ? 'Your moments will appear here' : context.language === 'German' ? 'Deine Momente werden hier erscheinen' : context.language === 'French' ? 'Vos moments apparaîtront ici' : 'I tuoi momenti appariranno qui',
                      recentActivity: context.language === 'Spanish' ? 'Actividad reciente' : context.language === 'English' ? 'Recent activity' : context.language === 'German' ? 'Letzte Aktivität' : context.language === 'French' ? 'Activité récente' : 'Attività recente',
                      significantMoments: context.language === 'Spanish' ? 'momentos clave' : context.language === 'English' ? 'key moments' : context.language === 'German' ? 'wichtige Momente' : context.language === 'French' ? 'moments clés' : 'momenti significativi',
                      continueRecording: context.language === 'Spanish' ? 'Continúa registrando momentos para ver tus patrones' : context.language === 'English' ? 'Keep recording moments to see your patterns' : context.language === 'German' ? 'Erfasse weiter Momente, um deine Muster zu sehen' : context.language === 'French' ? 'Continuez à enregistrer des moments pour voir vos modèles' : 'Continua a registrare momenti per vedere i tuoi pattern'
                    }}
                  />
                  */}
                </div>
              )}

              {/* Vital Balance Radar */}
              <div className="bg-[#1D1D1F] rounded-[32px] lg:rounded-[40px] p-6 lg:p-8 text-white shadow-2xl">
                <h3 className="text-[18px] font-black tracking-tight mb-6">{t.vitalBalance}</h3>
                <div className="h-[220px] lg:h-[240px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                      <PolarGrid stroke="rgba(255,255,255,0.1)" />
                      <PolarAngleAxis dataKey="subject" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 700 }} />
                      <Radar 
                        dataKey="A" 
                        stroke="#8b5cf6" 
                        fill="url(#radarGradient)" 
                        fillOpacity={0.6}
                        strokeWidth={2}
                      />
                      <defs>
                        <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#6366f1" />
                          <stop offset="100%" stopColor="#a855f7" />
                        </linearGradient>
                      </defs>
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Weekly Rhythm Heatmap */}
            {entries.length >= 5 && (
              <div className="stagger-6">
                <WeeklyRhythmChart 
                  data={weeklyRhythmData} 
                  language={context.language}
                  translations={{
                    title: t.insights.rhythmTitle,
                    subtitle: t.insights.rhythmSubtitle,
                    bestMoment: t.insights.rhythmBestMoment,
                    levelLow: t.insights.levelLow,
                    levelMedium: t.insights.levelMedium,
                    levelHigh: t.insights.levelHigh
                  }}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'personality' && (
          <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
            <div className="bg-white rounded-[32px] p-8 shadow-sm space-y-8">
              <div className="space-y-6">
                {[
                  { key: 'warmth', label: t.warmth, icon: Heart },
                  { key: 'directness', label: t.directness, icon: Zap },
                  { key: 'depth', label: t.depth, icon: Compass }
                ].map((item) => (
                  <div key={item.key} className="space-y-4">
                    <div className="flex justify-between items-center px-1">
                      <div className="flex items-center gap-2">
                        <item.icon size={16} className="text-indigo-500" />
                        <span className="text-[11px] font-black uppercase tracking-widest">{item.label}</span>
                      </div>
                      <span className="text-[14px] font-black text-indigo-600">{(currentPersonality as any)[item.key]}%</span>
                    </div>
                    <input 
                      type="range" 
                      min="0" max="100" 
                      value={(currentPersonality as any)[item.key]}
                      onChange={(e) => updatePersonality(item.key as any, parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                    />
                  </div>
                ))}
              </div>
              <div className="p-4 bg-gray-50 rounded-2xl flex gap-3 items-start border border-black/5">
                <Info size={14} className="text-black/20 mt-1 shrink-0" />
                <p className="text-[11px] font-medium text-black/40 leading-snug">{t.personalityNotice}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
             <div className="bg-white rounded-[32px] p-6 shadow-sm divide-y divide-black/5">
                <div className="py-4 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Globe size={18} className="text-black/30" />
                    <span className="font-bold">{t.interfaceLang}</span>
                  </div>
                  <select 
                    value={context.language} 
                    onChange={(e) => onUpdateContext({...context, language: e.target.value as Language})}
                    className="bg-transparent font-black text-indigo-600 outline-none appearance-none"
                  >
                    {LANGUAGES.map(l => <option key={l.id} value={l.id}>{l.flag} {l.label}</option>)}
                  </select>
                </div>
                <div className="py-4 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <UserCheck size={18} className="text-black/30" />
                    <span className="font-bold">{t.insights.userName}</span>
                  </div>
                  <input 
                    className="text-right font-black text-indigo-600 outline-none w-32"
                    value={context.userName}
                    onChange={(e) => onUpdateContext({...context, userName: e.target.value})}
                  />
                </div>
             </div>

             <button 
                onClick={() => setIsLogoutOpen(true)}
                className="w-full h-16 bg-indigo-50 text-indigo-600 rounded-[24px] font-black text-[12px] uppercase tracking-widest flex items-center justify-center gap-3 border border-indigo-100 active:scale-95 hover:bg-indigo-100 transition-all"
             >
                <LogOut size={16} /> {t.insightsPage.logout}
             </button>

             <button
                onClick={() => setIsDeleteOpen(true)}
                className="w-full h-16 bg-rose-50 text-rose-600 rounded-[24px] font-black text-[12px] uppercase tracking-widest flex items-center justify-center gap-3 border border-rose-100 active:scale-95 hover:bg-rose-100 transition-all"
             >
                <Trash2 size={16} /> {t.resetSystem}
             </button>
          </div>
        )}
        </div>
      </div>

      <LogoutModal
        isOpen={isLogoutOpen}
        onConfirm={handleLogout}
        onCancel={() => setIsLogoutOpen(false)}
        title="Logout"
        subtitle={t.insightsPage.logoutConfirm}
        description={t.insightsPage.logoutDescription}
        confirmText={t.insightsPage.logout}
        cancelText={t.common.back}
      />

      {/* Delete All Data Modal */}
      <LogoutModal
        isOpen={isDeleteOpen}
        onConfirm={handleDeleteAllData}
        onCancel={() => !isDeleting && setIsDeleteOpen(false)}
        title={deleteT.title}
        subtitle={deleteT.subtitle}
        description={deleteT.description}
        confirmText={isDeleting ? deleteT.deleting : deleteT.confirm}
        cancelText={deleteT.cancel}
      />

      {currentDiscoveryStep && (
        <FeatureDiscovery
          featureKey={currentDiscoveryStep}
          discoveryState={discoveryState}
          onDismiss={completeStep}
          onActivate={handleDiscoveryActivate}
          onNavigate={onNavigate}
          currentView="insights"
          language={context.language}
          position="bottom"
        />
      )}

      {/* AI Personality Selector Modal */}
      <PersonalitySelectorModal
        isOpen={isPersonalityModalOpen}
        onClose={() => setIsPersonalityModalOpen(false)}
        currentPersonalityKey={currentPersonalityKey}
        onSelect={onPersonalityChange!}
        language={context.language}
      />
    </div>
  );
};

export default InsightsView;
